import React, { useLayoutEffect } from "react";
import styled from "@emotion/styled";
import { getPanelGroupElement, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import useMeasure from "react-use/lib/useMeasure";
import { observer } from "mobx-react-lite";
import { MDSPanel } from "@/mds";
import { useAppStore } from "@/store";
import { TabBarContextProvider, TabBar } from "@/components/layout/components/tab-bar";
import { CenterPanelRouter } from "@/app/router/CenterPanelRouter";
import { TabProvider } from "@/components/layout/tab-context";
import { MDSSidebarWidths } from "@/mds/foundations";
import { useAutoCloseSidebarEffect } from "@/components/layout/components/center-panel-split-view/hooks";

const MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID = "main-panel-split-view-panel-group";

export const CenterPanelSplitView = observer(function CenterPanelSplitView() {
  const { store } = useAppStore();
  const { tabsMap, panes } = store.navigation.tabs;
  const [setContainerRef, { width: measuredWidth }] = useMeasure<HTMLElement>();
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [savedScrollPosition, setSavedScrollPosition] = React.useState<number>(0);

  const effectiveWidth = containerWidth > 0 ? containerWidth : measuredWidth;
  const minSize =
    effectiveWidth > 0 ? (store.navigation.tabs.MIN_PANE_WIDTH / effectiveWidth) * 100 : 20;

  const { keepSplitLockRef } = useAutoCloseSidebarEffect(effectiveWidth, store);

  useLayoutEffect(() => {
    const el = getPanelGroupElement(MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID);
    if (el) {
      setContainerRef(el);
      setContainerWidth(el.offsetWidth);
      const resizeObserver = new ResizeObserver(entries => {
        requestAnimationFrame(() => {
          for (const entry of entries) {
            const newWidth = entry.contentRect.width;
            setContainerWidth(newWidth);
          }
        });
      });
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    }
  }, [setContainerRef]);

  // Predict responsive behavior: if two panes are open and width is too small,
  // prefer closing sidebar first (simulate reclaimed width) instead of collapsing panes.
  const twoPanesOpenForPrediction = store.navigation.tabs.panes.length >= 2;
  const tooNarrowNow =
    effectiveWidth > 0 && effectiveWidth < store.navigation.tabs.MIN_CONTAINER_WIDTH;
  const predictedWidth =
    twoPanesOpenForPrediction && tooNarrowNow && store.interface.isSidebarOpen
      ? effectiveWidth + MDSSidebarWidths.expanded
      : effectiveWidth;

  const responsive = store.navigation.tabs.getResponsivePanes(predictedWidth);
  let visiblePanes = responsive.visiblePanes;
  let hiddenPanes = responsive.hiddenPanes;
  let shouldHidePanes = responsive.shouldHidePanes;

  // While we are collapsing the sidebar, keep center panes split to avoid
  // flashing to single pane during the animation.
  if (keepSplitLockRef.current && store.navigation.tabs.panes.length >= 2) {
    visiblePanes = store.navigation.tabs.panes;
    hiddenPanes = [];
    shouldHidePanes = false;
  }
  const canShowSplitButton = !shouldHidePanes && panes.length < store.navigation.tabs.MAX_PANES;
  const showResizeHandles = !shouldHidePanes;

  const paneCountForDefault = visiblePanes.length || 1;
  const defaultPaneSize = Math.max(100 / paneCountForDefault, minSize);

  return (
    <TabBarContextProvider>
      <Container id={MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID} direction="horizontal">
        {visiblePanes.map((pane, index) => {
          const paneActiveTab = pane.activeTabId ? tabsMap.get(pane.activeTabId) : null;
          const isLastPane = index === visiblePanes.length - 1;
          if (!paneActiveTab) return null;
          return (
            <React.Fragment key={pane.id}>
              <StyledPanel
                id={pane.id}
                order={pane.order}
                minSize={minSize}
                defaultSize={defaultPaneSize}
                onClick={_e => store.navigation.tabs.setPaneFocused(pane.id)}
              >
                <TabProvider
                  activeTab={paneActiveTab}
                  activeTabPath={paneActiveTab.path}
                  routerLocationState={paneActiveTab.state}
                >
                  <TabBar
                    pane={pane}
                    hiddenPanes={hiddenPanes}
                    tabsMap={tabsMap}
                    canShowSplitButton={canShowSplitButton}
                    savedScrollPosition={savedScrollPosition}
                    onSavedScrollPositionChange={setSavedScrollPosition}
                  />
                  <CenterPanelWrapper>
                    <CenterPanelRouter tab={paneActiveTab} />
                  </CenterPanelWrapper>
                </TabProvider>
              </StyledPanel>
              {!isLastPane && showResizeHandles && (
                <StyledPanelResizeHandle>
                  <ResizeHandle />
                </StyledPanelResizeHandle>
              )}
            </React.Fragment>
          );
        })}
      </Container>
    </TabBarContextProvider>
  );
});

const Container = styled(PanelGroup)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: row;
  gap: 2px;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
`;

const StyledPanel = styled(MDSPanel)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledPanelResizeHandle = styled(PanelResizeHandle)`
  border-radius: 100px;
  cursor: ew-resize;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  &:hover, &[data-resize-handle-state="hover"], &[data-resize-handle-state="drag"] {
    background: ${({ theme }) => theme.colors.surface.pane};
    transition: 0.2s ease-in;
    border-color: ${({ theme }) => theme.colors.border.default};

    "> div": {
      border-color: ${({ theme }) => theme.colors.border.default};
    },
  },
`;

const ResizeHandle = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  overflow: hidden;
  width: 2px;
`;

const CenterPanelWrapper = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0;
  background-color: ${({ theme }) => theme.colors.surface.pane};
`;
